import { PHASE } from "../../common/constants.ts";
import { idb } from "../db/index.ts";
import { g, helpers } from "../util/index.ts";
import { getClient, getLeagueIdForLid } from "./index.ts";
import { syncNow } from "./sync.ts";

// Payload shape for the 'release' event (applied by every client in sync.ts).
export type ReleaseEventPayload = {
	pid: number;
	tid: number;
	// The released contract - dead money the old team keeps paying (also the
	// player's asking demand as a free agent)
	contract: { amount: number; exp: number };
	// Whether to record dead money (false if just drafted or already expired)
	addDeadMoney: boolean;
	// If just drafted (pre-season), the salary is wiped and nothing is owed
	justDrafted: boolean;
	phase: number;
	season: number;
};

// Release one of your own players (multiplayer). Mirrors core/player/release:
// the player becomes a free agent and, unless he was just drafted / already
// expired, the team keeps paying his contract as dead money. The 'release'
// event carries everything needed to apply this deterministically everywhere,
// and the RPC frees his ownership row so he can be re-signed. Returns undefined
// for non-multiplayer leagues so the caller falls through to vanilla release.
export const mpReleasePlayer = async (
	pid: number,
): Promise<{ status: string } | undefined> => {
	const lid = g.get("lid");
	if (typeof lid !== "number") {
		return undefined;
	}
	const leagueId = await getLeagueIdForLid(lid);
	if (!leagueId) {
		return undefined; // single player league
	}

	const p = await idb.cache.players.get(pid);
	if (!p) {
		return { status: "not_found" };
	}
	if (p.tid !== g.get("userTid")) {
		return { status: "not_your_player" };
	}

	const season = g.get("season");
	const phase = g.get("phase");
	const justDrafted = helpers.justDrafted(p, phase, season);
	// Same condition as core/player/release: owe the contract unless it has
	// already expired
	const addDeadMoney =
		!justDrafted &&
		(p.contract.exp > season ||
			(p.contract.exp === season && phase < PHASE.PLAYOFFS));

	const event: ReleaseEventPayload = {
		pid,
		tid: p.tid,
		contract: { amount: p.contract.amount, exp: p.contract.exp },
		addDeadMoney,
		justDrafted,
		phase,
		season,
	};

	const { data, error } = await getClient().rpc("release_player", {
		p_league_id: leagueId,
		p_pid: pid,
		p_event: event,
	});
	if (error) {
		throw new Error(error.message);
	}
	if (data.status === "released") {
		await syncNow(leagueId);
	}
	return data;
};
